/**
 * Project Picker for Puck Editor
 *
 * Links a gallery project tile to a photo-library project (BusinessProject).
 * While linked, the tile pulls the project's current photos live; clearing the
 * link stops following the library. Same host contract as ImagePicker:
 * window.__SERVICEPRO_CONFIG__ for businessId/apiUrl, bearer token from
 * localStorage, inline styles on CSS variables so both editor themes work.
 */

import React, { useEffect, useState } from "react";
import { FieldLabel } from "@puckeditor/core";

interface ProjectPickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}

interface ProjectOption {
  id: string;
  title: string;
  photoCount: number;
}

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: "6px",
  border: "1px solid var(--puck-color-grey-09, #d1d5db)",
  background: "var(--puck-color-white, #ffffff)",
  color: "var(--puck-color-black, #111827)",
  fontSize: "14px",
};

const noteStyle: React.CSSProperties = {
  marginTop: "6px",
  fontSize: "12px",
  lineHeight: 1.5,
  color: "var(--puck-color-grey-05, #6b7280)",
};

export const ProjectPicker: React.FC<ProjectPickerProps> = ({ value = "", onChange, label }) => {
  const safeValue = typeof value === "string" ? value : "";
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const config = (
          window as { __SERVICEPRO_CONFIG__?: { businessId?: string; apiUrl?: string } }
        ).__SERVICEPRO_CONFIG__;
        if (!config?.businessId || !config?.apiUrl) {
          throw new Error("Site configuration not found");
        }
        const response = await fetch(
          `${config.apiUrl}/api/sites/businesses/${config.businessId}/projects`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
        );
        if (!response.ok) {
          throw new Error("Failed to fetch projects");
        }
        const data = (await response.json()) as { projects?: ProjectOption[] };
        if (!cancelled) {
          setProjects(data.projects ?? []);
          setStatus("ready");
        }
      } catch {
        if (!cancelled) {
          setStatus("error");
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const knownValue = !safeValue || projects.some((project) => project.id === safeValue);

  const body = (
    <div>
      <select
        value={safeValue}
        onChange={(event) => onChange(event.target.value)}
        style={selectStyle}
        disabled={status === "loading"}
      >
        <option value="">Not synced</option>
        {projects.map((project) => (
          <option key={project.id} value={project.id}>
            {project.title} ({project.photoCount} photos)
          </option>
        ))}
        {!knownValue ? <option value={safeValue}>Synced project (deleted?)</option> : null}
      </select>
      {status === "error" ? (
        <div style={noteStyle}>Could not load your projects. Try reloading the editor.</div>
      ) : safeValue ? (
        <div style={noteStyle}>
          Photos pull live from this project. Pick "Not synced" to stop following it; the photos
          saved on this tile take over.
        </div>
      ) : (
        <div style={noteStyle}>
          Pick a project from your photo library to fill this tile and keep it up to date.
        </div>
      )}
    </div>
  );

  if (!label) {
    return body;
  }
  return (
    <FieldLabel label={label} el="div">
      {body}
    </FieldLabel>
  );
};
